import { APIHomeWidgetFiguresSlice } from './homeWidgetFigures';
import { portalAPI } from './portalAPI';

import {
    HOME_WIDGET_FORM_SIZES,
    type Home,
    type HomeEntry,
    type HomeLayoutSave,
    type HomeWidget,
    type HomeWidgetSave,
} from 'types/homeWidgets';

// A stored widget's figure is cached by its id, so a changed definition has to ask for it again.
APIHomeWidgetFiguresSlice.enhanceEndpoints({
    addTagTypes: ['HomeWidgetFigure'],
    endpoints: {
        getWidgetFigure: { providesTags: (_figure, _error, { id }) => [{ type: 'HomeWidgetFigure', id }] },
    },
});

/** Home's layout, and the widgets a person built. */
export const APIHomeSlice = portalAPI
    .enhanceEndpoints({ addTagTypes: ['Home', 'HomeWidgets', 'HomeWidgetFigure'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getHome: builder.query<Home, void>({
                query: () => '/v1/home',
                providesTags: ['Home'],
            }),
            saveHomeLayout: builder.mutation<Home, HomeLayoutSave>({
                query: (body) => ({ url: '/v1/home/layout', method: 'PUT', body }),
                // A refused layout may hold a widget at a size its form no longer allows, its form changed since
                // Home was read: Home is read again, so the next save is built from what the server holds now.
                invalidatesTags: (_home, error) => (error ? ['Home'] : []),
                // The answer is the saved Home, so it replaces the cached one before the caller's `unwrap`
                // resolves: Home never shows the layout it had before the save.
                async onQueryStarted(_body, { dispatch, queryFulfilled }) {
                    try {
                        const { data } = await queryFulfilled;
                        dispatch(APIHomeSlice.util.updateQueryData('getHome', undefined, () => data));
                    } catch {
                        // The caller keeps what was arranged; the failure is reported where every request's is.
                    }
                },
            }),
            /** The widgets the person built, on their Home or not. */
            getHomeWidgets: builder.query<HomeWidget[], void>({
                query: () => '/v1/home-widgets',
                providesTags: ['HomeWidgets'],
            }),
            /** Builds a widget. It is on nobody's Home until a layout names it. */
            createHomeWidget: builder.mutation<HomeWidget, HomeWidgetSave>({
                query: (body) => ({ url: '/v1/home-widgets', method: 'POST', body }),
                invalidatesTags: ['HomeWidgets'],
            }),
            updateHomeWidget: builder.mutation<HomeWidget, HomeWidgetSave & { id: number }>({
                query: ({ id, ...body }) => ({ url: `/v1/home-widgets/${id}`, method: 'PUT', body }),
                invalidatesTags: (_widget, _error, { id }) => ['HomeWidgets', { type: 'HomeWidgetFigure', id }],
                // Home holds the widget itself, so the answer replaces it there at once, as a read of Home would
                // answer it: an entry whose size the new form does not allow takes the size the definition names.
                async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                    try {
                        const { data: widget } = await queryFulfilled;
                        const replace = (entry: HomeEntry): HomeEntry =>
                            entry.kind === 'WIDGET' && entry.widget.id === id
                                ? {
                                      ...entry,
                                      widget,
                                      size: HOME_WIDGET_FORM_SIZES[widget.definition.form].includes(entry.size)
                                          ? entry.size
                                          : widget.definition.size,
                                  }
                                : entry;
                        dispatch(
                            APIHomeSlice.util.updateQueryData('getHome', undefined, (home) => ({
                                ...home,
                                locked: home.locked.map(replace),
                                own: home.own.map(replace),
                            }))
                        );
                    } catch {
                        // Nothing changed; the failure is reported where every request's is.
                    }
                },
            }),
            /** Deletes a widget for good, which takes it off every Home that shows it. */
            deleteHomeWidget: builder.mutation<void, number>({
                query: (id) => ({ url: `/v1/home-widgets/${id}`, method: 'DELETE' }),
                invalidatesTags: ['HomeWidgets'],
                async onQueryStarted(id, { dispatch, queryFulfilled }) {
                    try {
                        await queryFulfilled;
                        const stays = (entry: HomeEntry) => entry.kind !== 'WIDGET' || entry.widget.id !== id;
                        dispatch(
                            APIHomeSlice.util.updateQueryData('getHome', undefined, (home) => ({
                                ...home,
                                locked: home.locked.filter(stays),
                                own: home.own.filter(stays),
                            }))
                        );
                    } catch {
                        // The widget is still there; the failure is reported where every request's is.
                    }
                },
            }),
        }),
    });

export const {
    useGetHomeQuery,
    useSaveHomeLayoutMutation,
    useGetHomeWidgetsQuery,
    useCreateHomeWidgetMutation,
    useUpdateHomeWidgetMutation,
    useDeleteHomeWidgetMutation,
} = APIHomeSlice;
